import { ErrorBoundary } from '@/components/ErrorBoundary/ErrorBoundary';
import Loading from '@/components/UI/loading/index.tsx';
import MarylandSherpa from '@/features/ApplicationForm/FormConfig/Maryland/Sherpa/index.tsx';
import OrganizationInfo from '@/features/STPDemo/OrganizationInfo';
import { usePermission } from '@/hooks/usePermission/index.tsx';
import GovernmentIntakeForm from '@/modules/governmentIntake';
import { CompletionScreen } from '@/modules/governmentIntake/components/completion-screen.tsx';
import DiscoveryOverview from '@/modules/governmentIntake/pages/public-overview/DiscoveryOverview.tsx';
import Unauthorized from '@/pages/Access/Unauthorized';
import Invite from '@/pages/Membership/Invite';
import '../css/app.css';
import * as Sentry from '@sentry/react';
import React, { Suspense, useMemo } from 'react';
import { createBrowserRouter, Outlet, RouterProvider, useNavigate } from 'react-router';

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);
import AppLayout from '../layouts/AppLayout';
import STPDemoLayout from '../layouts/STPDemoLayout';
import StaticLayout from '../layouts/StaticLayout';
import Applications from '../pages/Applications';
import Home from '../pages/Home';
import { Logout } from '../pages/Logout';
import PageNotFound from '../pages/PageNotFound';
import PublicMKEERADashboard from '../pages/PublicMKEERADashboard';
import StaticAuth from '../pages/StaticAuth';
import AppRoute from './AppRoute/index.tsx';
import OSRoute from './OSRoute/index.tsx';
import RequireAuth from './RequireAuth';
import RequireSystemAdmin from './RequireSystemAdmin';
import RequireTenantUser from './RequireTenantUser';

//Lazyloading
const PartnerSignUp = React.lazy(() => import('../pages/Auth/PartnerAuth/SignUp'));
const UserOnboard = React.lazy(() => import('../pages/UserOnboard'));
const ResetPassword = React.lazy(() => import('../pages/Auth/ResetPassword'));
const Nominee = React.lazy(() => import('../pages/Lottery/Nominee'));

// SME Onboarding Views (using default exports for React.lazy)
const InitialInfoView = React.lazy(() => import('../modules/smeOnboarding/pages/initial-info/InitialInfoView.tsx'));
const EmailSentView = React.lazy(() => import('../modules/smeOnboarding/pages/email-verification/EmailSentView.tsx'));
const EmailVerificationView = React.lazy(() => import('../modules/smeOnboarding/pages/email-verification/EmailVerificationView.tsx'));
const LandingView = React.lazy(() => import('../modules/smeOnboarding/pages/landing/LandingView.tsx'));
const DemoSchedulingView = React.lazy(() => import('../modules/smeOnboarding/pages/demo-scheduling/DemoSchedulingView.tsx'));
const OrgRegistrationView = React.lazy(() => import('../modules/smeOnboarding/pages/org-registration/OrgRegistrationView.tsx'));
const SingleRegistrationView = React.lazy(() => import('../modules/smeOnboarding/pages/single-registration/SingleRegistrationView.tsx'));
const ProductAssessmentView = React.lazy(() => import('../modules/smeOnboarding/pages/product-assessment/ProductAssessmentView.tsx'));
const PricingSetupView = React.lazy(() => import('../modules/smeOnboarding/pages/pricing-setup/PricingSetupView.tsx'));
const ReviewConfirmView = React.lazy(() => import('../modules/smeOnboarding/pages/review-confirm/ReviewConfirmView.tsx'));
const SetupCompleteView = React.lazy(() => import('../modules/smeOnboarding/pages/setup-complete/SetupCompleteView.tsx'));
const InteractiveTrainingView = React.lazy(() => import('../modules/smeOnboarding/pages/interactive-training/InteractiveTrainingView.tsx'));

function GovernmentIntakeCompletionRoute() {
  const navigate = useNavigate();

  return <CompletionScreen onNewSubmission={() => navigate('/government-intake/form')} />;
}

const getRoutes = (hasPermission: ReturnType<typeof usePermission>['hasPermission']) => {
  return sentryCreateBrowserRouter([
    {
      path: '/MKE-ERA/:applicationId',
      element: <PublicMKEERADashboard />,
    },
    {
      path: '/',
      element: <StaticLayout />,
      children: [
        {
          index: true,
          element: <Home />,
        },
        { path: '/logout', element: <Logout /> },
        { path: '/auth/sign_in', element: <Home /> },
        { path: '/auth/identity_auth', element: <StaticAuth /> },
        { path: '/access/unauthorized', element: <Unauthorized /> },
        { path: '*', element: <PageNotFound /> },
      ],
    },

    { path: '/applications/*', element: <Applications /> },
    { path: '/reset-password', element: <ResetPassword /> },
    { path: '/user-onboard', element: <UserOnboard /> },
    { path: '/client-onboard', element: <PartnerSignUp /> },

    { path: '/app/marylandSherpa', element: <MarylandSherpa /> },
    { path: '/member-onboard', element: <Invite /> },
    { path: '/lottery/:id/nominee', element: <Nominee /> },

    {
      path: '/government-intake',
      element: <Outlet />,
      children: [
        { index: true, element: <DiscoveryOverview /> },
        { path: 'form', element: <GovernmentIntakeForm /> },
        { path: 'completion', element: <GovernmentIntakeCompletionRoute /> },
      ],
    },

    // SME Onboarding routes (public)
    {
      path: '/onboarding',
      children: [
        { path: 'start', element: <InitialInfoView /> },
        { path: 'email-sent', element: <EmailSentView /> },
        { path: 'verify', element: <EmailVerificationView /> },
        { path: 'choose', element: <LandingView /> },
        { path: 'demo', element: <DemoSchedulingView /> },
        { path: 'organization/register', element: <OrgRegistrationView /> },
        { path: 'organization/assessment', element: <ProductAssessmentView /> },
        { path: 'organization/pricing', element: <PricingSetupView /> },
        { path: 'organization/review', element: <ReviewConfirmView /> },
        { path: 'organization/complete', element: <SetupCompleteView /> },
        { path: 'single-project/register', element: <SingleRegistrationView /> },
        { path: 'single-project/pricing', element: <PricingSetupView /> },
        { path: 'single-project/review', element: <ReviewConfirmView /> },
        { path: 'single-project/complete', element: <SetupCompleteView /> },
        { path: 'single-project/training', element: <InteractiveTrainingView /> },
      ],
    },

    {
      path: '/stpdemo',
      element: <STPDemoLayout />,
      children: [{ index: true, element: <OrganizationInfo /> }],
    },

    {
      element: <RequireAuth />,
      ErrorBoundary,
      children: [
        {
          element: <AppLayout />,
          children: [
            {
              path: '/terminal',
              element: <RequireTenantUser />,
              children: AppRoute(hasPermission),
            },
            {
              path: '/os',
              element: <RequireSystemAdmin />,
              children: OSRoute(hasPermission),
            },
          ],
        },
      ],
    },
  ]);
};

export default function Allroute() {
  const { hasPermission } = usePermission();

  const routes = useMemo(() => getRoutes(hasPermission), [hasPermission]);

  return (
    <Suspense fallback={<Loading loadingText='loading' />}>
      <RouterProvider router={routes} />
    </Suspense>
  );
}
